import Box from "@mui/material/Box";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import useStyles from "../../../../styles/styles";
import { configState } from "../../../../recoil/atoms";
import AppointmentForm from "../Forms/AppointmentForm";
import { editValidationSchema } from "../FormModel/validationSchema";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import { Button, Divider, Grid, LinearProgress, Typography } from "@mui/material";
import { AppointmentApi, EditAppointmentRequest, GetAppointmentResponse } from "../../../../api/medical-operations";

const moment = require('moment-timezone');

const EU_DST = (year: Number) : moment.Moment[] => {
  var start = moment(`UTC March 31 ${year}`); start.subtract(start.day(), "days");
  var end = moment(`UTC October 31 ${year}`); end.subtract(end.day(), "days");
  return [start, end]
};

const isDST = (date_utc: moment.Moment) => {
  const year = date_utc.year()
  const DST_bounds = {...(EU_DST(year))};
  return date_utc.isBetween(DST_bounds[0], DST_bounds[1], undefined, '[)')
};

interface GetAppointmentResponseExtended extends GetAppointmentResponse {
  Duration: string;
}

const EditAppointment = () => {
  const classes = useStyles();
  let { appointmentID } = useParams();
  const config = useRecoilValue(configState);

  const [loading, setLoading] = useState(false);
  const [theDateFrom, setTheDateFrom] = useState<Date>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [appointment, setAppointment] = useState<GetAppointmentResponse>();

  const getAppointment = () => {
    const appointment = new AppointmentApi(config);
    appointment
      .getAppointmentById(appointmentID!)
      .then((res) => {
        const dateFrom = new Date(res.data.DateFrom);
        const dateTo = new Date(res.data.DateTo);
  
        const localDateFrom = new Date(dateFrom.getTime() + dateFrom.getTimezoneOffset() * 60000);
        const localDateTo = new Date(dateTo.getTime() + dateTo.getTimezoneOffset() * 60000);
        setTheDateFrom(localDateFrom);
        const min = Math.round(Math.abs(localDateTo.getTime() - localDateFrom.getTime()) / 60000);

        setAppointment({
          ...res.data,
          DateFrom: localDateFrom,
          DateTo: localDateTo,
          Duration: min.toString()
        } as GetAppointmentResponseExtended);

        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };
  
  const editAppointment = (values: EditAppointmentRequest) => {
    setLoading(true);
    const Appointment = new AppointmentApi(config);
    Appointment.editAppointment(appointmentID!, values)
      .then((res) => {
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit = (values: any, actions: any) => {
    actions.setTouched({});
    actions.setSubmitting(false);
    
    const originalDateFrom = moment.tz(values.DateFrom, 'YYYY-MM-DDTHH:mm:ss.SSSZ', 'UTC');

    const targetTimeZone = 'UTC';
    const dateFrom = originalDateFrom.clone().tz(targetTimeZone);
    const dateTo = dateFrom.clone().add(parseInt(values.Duration), 'minutes');

    const now = moment();
    if (dateFrom.isAfter(now) && (values.Status === "Closed" || values.Status === "Missed")) {
      toast.error("Cannot set 'Closed' or 'Missed' status for future appointments");
      return;
    }

    const valuesEdited: EditAppointmentRequest = {
      ConsultationReason: values?.ConsultationReason,
      Status: values?.Status,
      DateTo: dateTo.toDate(),
      Notes: values?.Notes,
      Priority: values?.Priority,
      DateFrom: dateFrom.toDate(),
    };

    if (originalDateFrom.isSame(theDateFrom)) {     
      const add_to_dateFrom = isDST(dateFrom) ? 3 : 2;
      const add_to_dateTo = isDST(dateTo) ? 3 : 2;
      valuesEdited.DateFrom = dateFrom.add(add_to_dateFrom, 'hours').toDate();
      valuesEdited.DateTo = dateTo.add(add_to_dateTo, 'hours').toDate();
    }else{
      valuesEdited.DateFrom = dateFrom.add(0, 'hours').toDate();
      valuesEdited.DateTo = dateTo.add(0, 'hours').toDate();
    }
    editAppointment(valuesEdited);
  };
  

  useEffect(() => {
    getAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <MainCard>
        {!isLoading ? (
          <>
            <Formik
              initialValues={appointment!}
              enableReinitialize={true}
              validationSchema={editValidationSchema}
              onSubmit={handleSubmit}
            >
              {({ dirty }) => {
                return (
                  <Form>
                    <Box sx={{ width: "100%" }}>
                      <Box sx={{ bgcolor: "#fff", p: 4 }}>
                        <Typography variant="h3" gutterBottom sx={{ p: 0 }}>
                          {appointment?.Patient?.User?.FirstName.concat(
                            " ",
                            appointment?.Patient?.User?.FamilyName,
                            " "
                          )}
                          Appointment
                        </Typography>
                        <Divider />

                        <Grid
                          container
                          sx={{
                            p: 4,
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <AppointmentForm isEdit isClosed={appointment && (appointment.Status === "Closed" || moment().diff(moment(appointment.DateFrom), 'hours') > 48)}/>
                          <Grid item>
                            <Button
                              type="submit"
                              variant="contained"
                              color="secondary"
                              className={classes.button}
                              fullWidth
                              disabled={!dirty || loading}
                              onClick={() => handleSubmit}
                              startIcon={loading ? <CircularProgress size={20} /> : null}
                            >
                              {loading ? "Saving..." : "Save"}
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          </>
        ) : (
          <LinearProgress color="secondary" />
        )}
      </MainCard>
    </>
  );
};

export default EditAppointment;
